import { DialogUntitledUI, Modal, ModalOverlay } from "@components";
import { Button } from "@components/button/button-untitledui";
import { ArrowNarrowRight, CheckCircle } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { IMAGES } from "../assets";
import {
	INFRACTION_ONBOARDING_STEPS,
	INFRACTION_ONBOARDING_TOTAL_STEPS,
} from "../constants/infraction-onboarding.constant";
import type {
	CertificateFormErrors,
	CertificateFormValues,
} from "../types/infraction-certificate.types";
import {
	hasCertificateFormErrors,
	validateCertificateForm,
} from "../utils/validate-certificate-form";
import { InfractionCertificateConnectForm } from "./infraction-certificate-connect-form";
import { InfractionConnectSuccessView } from "./infraction-connect-success-view";
import { InfractionOnboardingConnectStep } from "./infraction-onboarding-connect-step";

type OnboardingView = "slideshow" | "connect" | "certificate" | "success";

type InfractionOnboardingModalProps = {
	isOpen: boolean;
	slideshowStep: number;
	startAtConnect?: boolean;
	companyName: string | null;
	companyDocument: string | null;
	onSlideshowStepChange: (step: number) => void;
	onClose: () => void;
	onSkip: () => void;
	onManualContinue: () => void;
	onConnectSuccess: () => void;
	onMarkOnboardingSeen: () => Promise<void>;
};

const INITIAL_CERTIFICATE_VALUES: CertificateFormValues = {
	certificateFile: null,
	certificatePassword: "",
};

function OnboardingIllustration() {
	return (
		<div
			aria-hidden
			className="flex h-64 w-full items-center justify-center rounded-t-2xl bg-brand-25"
		>
			{/* biome-ignore lint/performance/noImgElement: asset estático do onboarding */}
			<img
				alt="Ilustração do onboarding"
				className="h-40 w-40 object-cover"
				height={160}
				src={IMAGES.IllustrationOnboarding}
				width={160}
			/>
		</div>
	);
}

function SlideshowContent({
	slideshowStep,
	onSkip,
	onAdvance,
	isLastSlideshowStep,
	isMarkingOnboardingSeen,
}: {
	slideshowStep: number;
	onSkip: () => void;
	onAdvance: () => void;
	isLastSlideshowStep: boolean;
	isMarkingOnboardingSeen: boolean;
}) {
	const content = INFRACTION_ONBOARDING_STEPS[slideshowStep - 1];

	if (!content) {
		return null;
	}

	return (
		<>
			<OnboardingIllustration />
			<DialogUntitledUI
				aria-label="Onboarding gestão de infrações"
				className="flex w-full max-w-[520px] flex-col items-center px-10 pt-8 pb-6"
			>
				<h2 className="text-center font-bold text-2xl text-secondary">
					{content.title}
				</h2>
				<p className="mt-2 max-w-[340px] text-center text-secondary text-sm">
					{content.description}
				</p>
				<div
					aria-label={`Passo ${slideshowStep} de ${INFRACTION_ONBOARDING_TOTAL_STEPS}`}
					className="mt-6 flex items-center justify-center gap-2"
					role="tablist"
				>
					{INFRACTION_ONBOARDING_STEPS.map((_, index) => {
						const stepNumber = index + 1;
						const isActive = slideshowStep === stepNumber;
						return (
							<span
								{...(isActive ? { "aria-current": "step" as const } : {})}
								className={twMerge(
									"rounded-full transition-all",
									isActive ? "h-1.5 w-6 bg-brand-600" : "size-1.5 bg-gray-300"
								)}
								key={stepNumber}
							/>
						);
					})}
				</div>
				<div className="mt-6 flex w-full items-center justify-center gap-4">
					<Button
						aria-label="Pular onboarding"
						className="flex-2/5 items-center justify-center"
						color="link-gray"
						onClick={onSkip}
						size="md"
						tabIndex={0}
					>
						Pular
					</Button>
					<Button
						aria-label={isLastSlideshowStep ? "Conectar empresa" : "Avançar"}
						className="flex-3/5"
						color="primary"
						iconTrailing={
							isLastSlideshowStep ? (
								<CheckCircle className="size-5 text-white" />
							) : (
								<ArrowNarrowRight className="size-5 text-white" />
							)
						}
						isDisabled={isMarkingOnboardingSeen}
						isLoading={isMarkingOnboardingSeen}
						onClick={onAdvance}
						size="md"
						tabIndex={0}
					>
						{isLastSlideshowStep ? "Conectar empresa" : "Avançar"}
					</Button>
				</div>
				<div className="mt-6 w-full border-[#EDEEEF] border-t pt-4">
					<p className="text-center text-quaternary text-xs">
						<span className="font-bold">
							Benefícios de conectar sua empresa ao nosso sistema
						</span>{" "}
						• Passo {slideshowStep} de {INFRACTION_ONBOARDING_TOTAL_STEPS}
					</p>
				</div>
			</DialogUntitledUI>
		</>
	);
}

export function InfractionOnboardingModal({
	isOpen,
	slideshowStep,
	startAtConnect = false,
	companyName,
	companyDocument,
	onSlideshowStepChange,
	onClose,
	onSkip,
	onManualContinue,
	onConnectSuccess,
	onMarkOnboardingSeen,
}: InfractionOnboardingModalProps) {
	const [view, setView] = useState<OnboardingView>("slideshow");
	const [certificateValues, setCertificateValues] =
		useState<CertificateFormValues>(INITIAL_CERTIFICATE_VALUES);
	const [certificateErrors, setCertificateErrors] =
		useState<CertificateFormErrors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isMarkingOnboardingSeen, setIsMarkingOnboardingSeen] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setView(startAtConnect ? "connect" : "slideshow");
			setCertificateValues(INITIAL_CERTIFICATE_VALUES);
			setCertificateErrors({});
		}
	}, [isOpen, startAtConnect]);

	const isLastSlideshowStep =
		slideshowStep === INFRACTION_ONBOARDING_TOTAL_STEPS;

	const handleAdvanceSlideshow = async () => {
		if (isLastSlideshowStep) {
			setIsMarkingOnboardingSeen(true);
			try {
				await onMarkOnboardingSeen();
			} finally {
				setIsMarkingOnboardingSeen(false);
			}
			setView("connect");
			return;
		}

		onSlideshowStepChange(slideshowStep + 1);
	};

	const handleCloseAll = () => {
		setView("slideshow");
		setCertificateValues(INITIAL_CERTIFICATE_VALUES);
		setCertificateErrors({});
		onClose();
	};

	const handleSkip = () => {
		handleCloseAll();
		onSkip();
	};

	const handleManualContinue = () => {
		handleCloseAll();
		onManualContinue();
	};

	const handleCertificateSubmit = async () => {
		const errors = validateCertificateForm(certificateValues);
		setCertificateErrors(errors);

		if (hasCertificateFormErrors(errors)) {
			return;
		}

		setIsSubmitting(true);
		await new Promise((resolve) => {
			setTimeout(resolve, 600);
		});
		setIsSubmitting(false);
		setView("success");
	};

	const handleAccessModule = () => {
		handleCloseAll();
		onConnectSuccess();
	};

	const modalMaxWidth =
		view === "certificate" ? "max-w-[640px]" : "max-w-[520px]";

	return (
		<ModalOverlay
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					handleCloseAll();
				}
			}}
		>
			<Modal
				className={twMerge(
					"relative mx-auto h-fit w-full overflow-hidden rounded-2xl bg-bg-primary p-0",
					modalMaxWidth
				)}
			>
				{view === "slideshow" ? (
					<SlideshowContent
						isLastSlideshowStep={isLastSlideshowStep}
						isMarkingOnboardingSeen={isMarkingOnboardingSeen}
						onAdvance={handleAdvanceSlideshow}
						onSkip={handleSkip}
						slideshowStep={slideshowStep}
					/>
				) : null}

				{view === "connect" ? (
					<InfractionOnboardingConnectStep
						onClose={handleCloseAll}
						onContinueManual={handleManualContinue}
						onSendCertificate={() => {
							setView("certificate");
						}}
					/>
				) : null}

				{view === "certificate" ? (
					<InfractionCertificateConnectForm
						companyDocument={companyDocument}
						companyName={companyName}
						errors={certificateErrors}
						isSubmitting={isSubmitting}
						onCancel={() => {
							setView("connect");
							setCertificateErrors({});
						}}
						onChange={(partial) => {
							setCertificateValues((prev) => ({ ...prev, ...partial }));
							setCertificateErrors({});
						}}
						onClose={handleCloseAll}
						onSubmit={handleCertificateSubmit}
						values={certificateValues}
					/>
				) : null}

				{view === "success" ? (
					<InfractionConnectSuccessView
						onAccessModule={handleAccessModule}
						onClose={handleCloseAll}
					/>
				) : null}
			</Modal>
		</ModalOverlay>
	);
}
